import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Card, Callout, Spinner, Badge, Select, Modal } from "@/design/components";
import { getUsage } from "@/api/admin";
import {
  listInvoices,
  startCheckout,
  goToCheckout,
  openBillingPortal,
  goToBillingPortal,
  type Invoice,
  type PaidTierId,
} from "@/api/billing";
import { ApiError } from "@/api/client";
import { TIERS, TIER_LABEL } from "@/features/onboarding/tiers";
import type { TierId } from "@/api/auth";
import { downloadCsv } from "@/lib/csv";
import { DunningBanner } from "./DunningBanner";
import styles from "./BillingScreen.module.css";

const apiMessage = (err: unknown, fallback: string): string =>
  err instanceof ApiError ? err.message : fallback;

function formatMoney(amountMinor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency.toUpperCase() }).format(
      amountMinor / 100,
    );
  } catch {
    // Unknown currency code → fall back to a plain major-unit number.
    return `${(amountMinor / 100).toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

const STATUS_TONE: Record<string, "success" | "warning" | "danger" | "neutral"> = {
  paid: "success",
  open: "warning",
  uncollectible: "danger",
  void: "neutral",
  draft: "neutral",
};

const PAID_TIERS = TIERS.filter((t) => t.paid);

// Tier ordering for "the next upgrade" default in the change-plan picker.
const TIER_ORDER: Record<TierId, number> = { free: 0, small: 1, medium: 2, large: 3, xlarge: 4 };

/**
 * Billing (Epic 5) — the current plan, a Stripe-Checkout change-plan flow, and
 * invoice history. Tier comes from /admin/usage; invoices from /billing/invoices
 * (resolved on-demand from the org's Stripe subscription).
 */
export function BillingScreen() {
  const usageQuery = useQuery({ queryKey: ["usage"], queryFn: ({ signal }) => getUsage(signal) });
  const invoicesQuery = useQuery({ queryKey: ["invoices"], queryFn: ({ signal }) => listInvoices(signal) });

  const [changeOpen, setChangeOpen] = useState(false);
  const currentTier = usageQuery.data?.tier;
  // Default the picker to the lowest paid tier ABOVE the current one (the natural upgrade).
  const defaultTarget: PaidTierId =
    (PAID_TIERS.find((t) => TIER_ORDER[t.id] > TIER_ORDER[currentTier ?? "free"])?.id as PaidTierId) ?? "small";
  const [target, setTarget] = useState<PaidTierId>(defaultTarget);

  const checkout = useMutation({
    mutationFn: (tier: PaidTierId) => startCheckout(tier),
    onSuccess: (url) => goToCheckout(url), // leaves the SPA for Stripe-hosted checkout
  });

  // Self-serve management (cancel/downgrade/update card) via the Stripe Billing Portal.
  const portal = useMutation({
    mutationFn: () => openBillingPortal(),
    onSuccess: (url) => goToBillingPortal(url),
  });

  function openChange() {
    checkout.reset();
    setTarget(defaultTarget);
    setChangeOpen(true);
  }
  function closeChange() {
    if (checkout.isPending) return; // a redirect is imminent — don't drop it
    setChangeOpen(false);
  }

  const tier = currentTier ? TIERS.find((t) => t.id === currentTier) : undefined;
  const invoices = invoicesQuery.data ?? [];

  function exportInvoicesCsv(): void {
    downloadCsv(
      `cogent-invoices-${new Date().toISOString().slice(0, 10)}.csv`,
      ["Date", "Invoice", "Amount", "Currency", "Status"],
      invoices.map((inv) => [
        formatDate(inv.created),
        inv.number ?? "",
        (inv.amount / 100).toFixed(2),
        inv.currency.toUpperCase(),
        inv.status ?? "",
      ]),
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="u-eyebrow">Control plane</p>
          <h1 className={styles.h1}>Billing</h1>
        </div>
      </header>

      {/* Dunning alert (past_due/unpaid) — renders nothing otherwise. */}
      {usageQuery.data && <DunningBanner status={usageQuery.data.subscription.status} />}

      {/* Current plan */}
      {usageQuery.isPending && (
        <div className={styles.loading}>
          <Spinner size={22} color="var(--accent)" /> <span>Loading your plan…</span>
        </div>
      )}
      {usageQuery.isError && (
        <Callout tone="danger" title="Couldn't load your plan">
          {apiMessage(usageQuery.error, "Please try again shortly.")}
        </Callout>
      )}
      {usageQuery.data && tier && (
        <Card className={styles.plan} featured>
          <div className={styles.planMain}>
            <p className="u-eyebrow">Current plan</p>
            <div className={styles.planName}>
              <h2>{TIER_LABEL[tier.id]}</h2>
              <Badge tone="accent">{tier.priceLabel}/mo</Badge>
            </div>
            <p className={styles.planCaps}>
              {tier.agents} agents · {tier.channels} channels
            </p>
            {portal.isError && (
              <p className={styles.planError} role="alert">
                {apiMessage(portal.error, "Couldn't open billing.")}
              </p>
            )}
          </div>
          <div className={styles.planActions}>
            <Button onClick={openChange}>{tier.paid ? "Change plan" : "Upgrade"}</Button>
            {/* Self-serve management is only possible once a Stripe subscription exists. */}
            {usageQuery.data.subscription.status && (
              <Button variant="ghost" loading={portal.isPending} onClick={() => portal.mutate()}>
                Cancel or manage billing
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Invoice history */}
      <section className={styles.invoices}>
        <div className={styles.invoicesHead}>
          <h2 className={styles.sectionTitle}>Invoices</h2>
          {invoices.length > 0 && (
            <Button variant="secondary" size="sm" onClick={exportInvoicesCsv}>
              Export CSV
            </Button>
          )}
        </div>
        {invoicesQuery.isPending && (
          <div className={styles.loading}>
            <Spinner size={20} color="var(--accent)" /> <span>Loading invoices…</span>
          </div>
        )}
        {invoicesQuery.isError && (
          <Callout tone="danger" title="Couldn't load invoices">
            {apiMessage(invoicesQuery.error, "Please try again shortly.")}
          </Callout>
        )}
        {invoicesQuery.data && invoicesQuery.data.length === 0 && (
          <Card className={styles.empty}>
            <p className={styles.emptyBlurb}>
              No invoices yet. Once you subscribe to a paid plan, your invoices appear here.
            </p>
          </Card>
        )}
        {invoicesQuery.data && invoicesQuery.data.length > 0 && (
          <Card className={styles.tableCard}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Invoice</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Status</th>
                  <th scope="col" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {invoicesQuery.data.map((inv) => (
                  <InvoiceRow key={inv.id} invoice={inv} />
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </section>

      {/* Change plan */}
      <Modal
        open={changeOpen}
        onClose={closeChange}
        title="Change your plan"
        footer={
          <>
            <Button variant="ghost" onClick={closeChange} disabled={checkout.isPending}>
              Cancel
            </Button>
            <Button loading={checkout.isPending} onClick={() => checkout.mutate(target)}>
              Continue to payment
            </Button>
          </>
        }
      >
        <p className={styles.modalIntro}>
          Pick a plan and continue to secure Stripe checkout. Your quota updates as soon as payment
          succeeds.
        </p>
        <Select
          label="Plan"
          value={target}
          onChange={(e) => setTarget(e.target.value as PaidTierId)}
          options={PAID_TIERS.map((t) => ({
            value: t.id,
            label: `${t.name} — ${t.priceLabel}/mo (${t.agents} agents, ${t.channels} channels)`,
          }))}
        />
        {checkout.isError && (
          <div className={styles.modalError}>
            <Callout tone="danger">{apiMessage(checkout.error, "Couldn't start checkout.")}</Callout>
          </div>
        )}
      </Modal>
    </div>
  );
}

function InvoiceRow({ invoice }: { invoice: Invoice }) {
  const tone = invoice.status ? (STATUS_TONE[invoice.status] ?? "neutral") : "neutral";
  return (
    <tr>
      <td>{formatDate(invoice.created)}</td>
      <td className={styles.mono}>{invoice.number ?? "—"}</td>
      <td>{formatMoney(invoice.amount, invoice.currency)}</td>
      <td>
        <Badge tone={tone}>{invoice.status ?? "—"}</Badge>
      </td>
      <td className={styles.actions}>
        {invoice.hostedInvoiceUrl && (
          <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noreferrer">
            View
          </a>
        )}
        {invoice.invoicePdf && (
          <a href={invoice.invoicePdf} target="_blank" rel="noreferrer">
            PDF
          </a>
        )}
      </td>
    </tr>
  );
}
